import { useId } from "react";
import type { CSSProperties } from "react";

import { cx } from "../classnames";
import styles from "./Section.module.css";

/**
 * Figma's "Section" component: a glass plate with a heading over a short list.
 * Every instance is the same size and the same shape — only where it sits
 * across the column changes.
 */
export const PLATE = {
  width: 200,
  cornerRadius: 20,
  paddingInline: 10,
  paddingBlock: 5,
  /** Between the heading and the list. */
  gap: 14,
  /** Between the lines of the list. */
  linesGap: 10,
} as const;

/** How a section sits across the main column. Figma's wrapper frame. */
export type SectionLayout = {
  /** The wrapper's `counterAxisAlignItems`. */
  readonly align: "start" | "center" | "end";
  /** Its `paddingLeft`, which nudges the plate off the column's margin. */
  readonly indent?: number;
};

export type SectionProps = {
  readonly heading: string;
  readonly lines: readonly string[];
  readonly layout: SectionLayout;
  /** The plate's fill. Bespoke to the page it is drawn on. */
  readonly plateClassName: string;
  readonly headingClassName: string;
  readonly lineClassName: string;
  /** The plate's backdrop blur, in design pixels. */
  readonly blur: number;
};

/**
 * How far past the plate its glass pane is drawn so the blur has real backdrop
 * to read. Three standard deviations covers all but a rounding error of a
 * Gaussian's weight; past about 60px Chrome starts approximating the backdrop
 * and the pane flattens to one colour, so the margin stops there.
 */
const samplingMargin = (blur: number) => Math.min(blur * 3, 60);

const ALIGN = { start: styles.start, center: styles.center, end: styles.end } as const;

/**
 * A section of the landing page: the plate, the heading, and the list.
 *
 * The wrapper fills the column and the plate is a fixed 200 inside it, so
 * "which side of the page is this on" is the only thing a caller decides.
 */
export function Section({
  heading,
  lines,
  layout,
  plateClassName,
  headingClassName,
  lineClassName,
  blur,
}: SectionProps) {
  const headingId = useId();
  const pad = samplingMargin(blur);

  const style = {
    "--indent": layout.indent ?? 0,
    "--width": PLATE.width,
    "--radius": PLATE.cornerRadius,
    "--pad-inline": PLATE.paddingInline,
    "--pad-block": PLATE.paddingBlock,
    "--gap": PLATE.gap,
    "--lines-gap": PLATE.linesGap,
    "--blur": blur,
    "--pad": pad,
  } as CSSProperties;

  return (
    <section
      className={cx(styles.section, ALIGN[layout.align])}
      style={style}
      aria-labelledby={headingId}
    >
      <div className={styles.plate} data-plate="">
        <div className={cx(styles.glass, plateClassName)} />

        <h2 id={headingId} className={cx(styles.heading, headingClassName)}>
          {heading}
        </h2>

        <ul className={styles.lines}>
          {lines.map((line) => (
            <li key={line} className={lineClassName}>
              {line}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
